import { useState, useEffect } from 'react';
import adminService from '../../services/admin';
import toast from 'react-hot-toast';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, searchQuery]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getUsers(page, 20, searchQuery);
      setUsers(data.users);
      setTotalPages(data.pages);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      await adminService.deactivateUser(userId);
      toast.success('User deactivated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await adminService.activateUser(userId);
      toast.success('User activated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleEditSubscription = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleSaveSubscription = async (data) => {
    try {
      await adminService.updateUserSubscription(selectedUser.id, data);
      toast.success('Subscription updated');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">User Management</h1>
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={handleSearch}
          className="input w-64"
        />
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="pb-3 px-4">Email</th>
              <th className="pb-3 px-4">Name</th>
              <th className="pb-3 px-4">Role</th>
              <th className="pb-3 px-4">Subscription</th>
              <th className="pb-3 px-4">Status</th>
              <th className="pb-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-gray-800">
                <td className="py-4 px-4">{user.email}</td>
                <td className="py-4 px-4">{user.full_name || '-'}</td>
                <td className="py-4 px-4">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      user.role === 'admin'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {user.role}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm text-gray-400">
                    {user.subscription?.tier
                      ? user.subscription.tier.charAt(0).toUpperCase() + user.subscription.tier.slice(1)
                      : 'Free'}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      user.is_active
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditSubscription(user)}
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      Edit
                    </button>
                    {user.is_active ? (
                      <button
                        onClick={() => handleDeactivate(user.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivate(user.id)}
                        className="text-green-400 hover:text-green-300 text-sm"
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-700">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="btn btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Subscription Modal */}
      {showEditModal && selectedUser && (
        <EditSubscriptionModal
          user={selectedUser}
          onSave={handleSaveSubscription}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
}

function EditSubscriptionModal({ user, onSave, onClose }) {
  const [formData, setFormData] = useState({
    tier: user.subscription?.tier || 'free',
    daily_limit: user.subscription?.daily_limit || 10,
    monthly_limit: user.subscription?.monthly_limit || 100,
    max_characters: user.subscription?.max_characters || 500,
    workspace_seats: user.subscription?.workspace_seats || 1,
    voice_cloning_enabled: user.subscription?.voice_cloning_enabled || false,
    max_cloned_voices: user.subscription?.max_cloned_voices || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="card w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Edit Subscription</h2>
        <p className="text-gray-400 mb-4">{user.email}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Tier</label>
            <select
              value={formData.tier}
              onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
              className="input w-full"
            >
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="scale">Scale</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Daily Limit</label>
              <input
                type="number"
                value={formData.daily_limit}
                onChange={(e) =>
                  setFormData({ ...formData, daily_limit: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Monthly Limit</label>
              <input
                type="number"
                value={formData.monthly_limit}
                onChange={(e) =>
                  setFormData({ ...formData, monthly_limit: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Max Characters</label>
            <input
              type="number"
              value={formData.max_characters}
              onChange={(e) =>
                setFormData({ ...formData, max_characters: parseInt(e.target.value) })
              }
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Workspace Seats</label>
            <input
              type="number"
              min="1"
              value={formData.workspace_seats}
              onChange={(e) =>
                setFormData({ ...formData, workspace_seats: parseInt(e.target.value, 10) || 1 })
              }
              className="input w-full"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="voice_cloning"
              checked={formData.voice_cloning_enabled}
              onChange={(e) =>
                setFormData({ ...formData, voice_cloning_enabled: e.target.checked })
              }
            />
            <label htmlFor="voice_cloning" className="text-sm">
              Voice Cloning Enabled
            </label>
          </div>

          {formData.voice_cloning_enabled && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Max Cloned Voices</label>
              <input
                type="number"
                value={formData.max_cloned_voices}
                onChange={(e) =>
                  setFormData({ ...formData, max_cloned_voices: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              Save
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserManagement;
